import React, { useState, useEffect } from 'react'
import Header from './Header.jsx'
import AppHead from './AppHead.jsx'
import { domain } from './Domain.jsx'
import useFetch from './useFetch.jsx'
import { useNavigate } from 'react-router-dom'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import Footer from './Footer.jsx'
import { motion, AnimatePresence } from 'framer-motion'
import { 
    Play, Search, Filter, Clock, Eye, BookOpen, Video, 
    TrendingUp, Star, Users, Calendar, ArrowRight, 
    ChevronDown, ChevronUp, Grid, List, Zap, Shield,
    CreditCard, MessageCircle, Settings, Smartphone,
    Globe, Target, Award, Lightbulb, HelpCircle
} from 'lucide-react'

const HowItWorkSkeleton = () => {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <SkeletonTheme baseColor="#f3f4f6" highlightColor="#e5e7eb">
        <Skeleton height={200} count={1} />
        <div className="p-6">
          <Skeleton height={24} count={1} className="mb-3" />
          <Skeleton height={16} count={2} className="mb-2" />
          <div className="flex gap-2 mt-4">
            <Skeleton height={20} width={60} count={1} />
            <Skeleton height={20} width={80} count={1} />
          </div>
        </div>
      </SkeletonTheme>
    </div>
  )
}

const How = ({ item, viewMode = 'grid' }) => {
  const [loaded, setLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate()
  
  const handleImageLoad = () => {
    setLoaded(true);
  };

  const truncateString = (str, maxLength = 120) => {
    if (!str || str.length <= maxLength) return str;
    const truncated = str.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    return lastSpaceIndex > -1 ? truncated.substring(0, lastSpaceIndex) + "..." : truncated + "...";
  };

  const formatDuration = (duration) => {
    if (!duration) return "5 min";
    const minutes = Math.floor(duration / 60);
    return `${minutes} min`;
  };

  const formatViews = (views) => {
    if (!views) return "0 views";
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M views`;
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K views`;
    return `${views} views`;
  };

  const getDifficultyColor = (difficulty) => {
    switch(difficulty?.toLowerCase()) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch(category?.toLowerCase()) {
      case 'getting-started': return <Zap className="w-4 h-4" />;
      case 'advanced': return <TrendingUp className="w-4 h-4" />;
      case 'troubleshooting': return <HelpCircle className="w-4 h-4" />;
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'security': return <Shield className="w-4 h-4" />;
      case 'payments': return <CreditCard className="w-4 h-4" />;
      default: return <BookOpen className="w-4 h-4" />;
    }
  };

  if (viewMode === 'list') {
    return (
      <motion.div
        className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-300"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        whileHover={{ y: -2 }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => navigate(`/how-it-works/${item.id}`)}
      >
        <div className="flex">
          <div className="relative w-48 h-32 flex-shrink-0">
            {!loaded && <Skeleton height="100%" />}
            <img
              src={domain + item.cover}
              alt={item.title}
              onLoad={handleImageLoad}
              style={{ display: loaded ? 'block' : 'none' }}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
              <div className="bg-white/90 rounded-full p-2">
                <Play className="w-5 h-5 text-gray-800" fill="currentColor" />
              </div>
            </div>
            <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
              {formatDuration(item.duration)}
            </div>
            {item.difficulty && (
              <div className={`absolute top-2 left-2 text-xs px-2 py-1 rounded-full ${getDifficultyColor(item.difficulty)}`}>
                {item.difficulty}
              </div>
            )}
          </div>
          <div className="flex-1 p-6">
            <div className="flex items-center gap-2 mb-2">
              {getCategoryIcon(item.category)}
              <span className="text-xs text-gray-500 uppercase tracking-wide">{item.category || 'General'}</span>
            </div>
            <h3 className="font-semibold text-gray-900 text-lg mb-2 line-clamp-2">
              {item.title}
            </h3>
            <p className="text-gray-600 text-sm mb-4 line-clamp-2">
              {truncateString(item.description, 150)}
            </p>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                <span>{formatViews(item.views)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>{formatDuration(item.duration)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>{new Date(item.created).toLocaleDateString()}</span>
              </div>
              {item.rating && (
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3 text-yellow-500 fill-current" />
                  <span>{item.rating}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-center w-16">
            <ArrowRight className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -5, scale: 1.02 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
              onClick={() => navigate(`/how-it-works/${item.id}`)}
    >
      <div className="relative">
        {!loaded && <Skeleton height={200} />}
        <img
          src={domain + item.cover}
          alt={item.title}
          onLoad={handleImageLoad}
          style={{ display: loaded ? 'block' : 'none' }}
          className="w-full h-48 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            className="bg-white/90 rounded-full p-3 backdrop-blur-sm"
            animate={{ scale: isHovered ? 1.1 : 1 }}
            transition={{ duration: 0.2 }}
          >
            <Play className="w-6 h-6 text-gray-800" fill="currentColor" />
          </motion.div>
        </div>
        <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
          {formatDuration(item.duration)}
        </div>
        {item.difficulty && (
          <div className={`absolute top-3 left-3 text-xs px-2 py-1 rounded-full ${getDifficultyColor(item.difficulty)}`}>
            {item.difficulty}
          </div>
        )}
        {item.featured && (
          <div className="absolute top-3 right-3 bg-gradient-to-r from-orange-400 to-orange-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <Star className="w-3 h-3 fill-current" />
            Featured
          </div>
        )}
      </div>
      
      <div className="p-6">
        <div className="flex items-center gap-2 mb-2">
          {getCategoryIcon(item.category)}
          <span className="text-xs text-gray-500 uppercase tracking-wide">{item.category || 'General'}</span>
        </div>
        <h3 className="font-semibold text-gray-900 text-lg mb-3 line-clamp-2">
          {item.title}
        </h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
          {truncateString(item.description, 120)}
        </p>
        
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            <span>{formatViews(item.views)}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{formatDuration(item.duration)}</span>
          </div>
          {item.rating && (
            <div className="flex items-center gap-1">
              <Star className="w-3 h-3 text-yellow-500 fill-current" />
              <span>{item.rating}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

const HowItWorks = () => {
  const navigate = useNavigate();
  const { data: tutorials, loading: tutorialsLoading } = useFetch(`${domain}/how-it-works`)
  const { data: stats, loading: statsLoading } = useFetch(`${domain}/statistics`)
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  
  const items = Array(8).fill(null);

  // Enhanced categories for filtering
  const categories = [
    { id: 'all', name: 'All Guides', icon: <BookOpen className="w-4 h-4" />, count: tutorials?.length || 0 },
    { id: 'getting-started', name: 'Getting Started', icon: <Zap className="w-4 h-4" />, count: tutorials?.filter(item => item.category === 'getting-started').length || 0 },
    { id: 'advanced', name: 'Advanced Features', icon: <TrendingUp className="w-4 h-4" />, count: tutorials?.filter(item => item.category === 'advanced').length || 0 },
    { id: 'troubleshooting', name: 'Troubleshooting', icon: <HelpCircle className="w-4 h-4" />, count: tutorials?.filter(item => item.category === 'troubleshooting').length || 0 },
    { id: 'mobile', name: 'Mobile App', icon: <Smartphone className="w-4 h-4" />, count: tutorials?.filter(item => item.category === 'mobile').length || 0 },
    { id: 'security', name: 'Security & Privacy', icon: <Shield className="w-4 h-4" />, count: tutorials?.filter(item => item.category === 'security').length || 0 },
    { id: 'payments', name: 'Payments & Billing', icon: <CreditCard className="w-4 h-4" />, count: tutorials?.filter(item => item.category === 'payments').length || 0 }
  ];

  // Filter data
  const filteredData = React.useMemo(() => {
    return tutorials?.filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    }) || [];
  }, [tutorials, searchTerm, selectedCategory]);

  // Featured tutorials
  const featuredTutorials = tutorials?.filter(item => item.featured) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Header />
      <AppHead
        title="How it Works - EventPlots Tutorials & Guides"
        description="Learn how to use EventPlots effectively with our comprehensive video tutorials and step-by-step guides. Master event planning, ticket management, and advanced features."
        imageUrl="https://eventplots.com/images/logo.png"
        url={window.location.href}
        twitterCardType="summary_large_image"
      />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="rounded-3xl p-8 mb-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="bg-orange-100 rounded-full p-2">
                  <Video className="w-6 h-6 text-orange-600" />
                </div>
                <span className="text-sm font-medium text-gray-700">Learning Hub</span>
              </div>
              <h1 className="text-lg font-bold mb-4 text-gray-900">
                Master EventPlots
              </h1>
              <p className="text-sm text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto">
                Unlock the full potential of event planning with our comprehensive video tutorials and step-by-step guides. 
                From basic setup to advanced features, we've got everything you need to succeed.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
                <div className="flex items-center gap-2 bg-orange-100 rounded-lg px-3 py-2">
                  <Video className="w-4 h-4 text-orange-600" />
                  <span className="text-orange-700 text-sm font-medium">{statsLoading ? '...' : `${stats?.total_tutorials || 0} Videos`}</span>
                </div>
                <div className="flex items-center gap-2 bg-blue-100 rounded-lg px-3 py-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-700 text-sm font-medium">{statsLoading ? '...' : `${stats?.total_users || 0} Users`}</span>
                </div>
                <div className="flex items-center gap-2 bg-yellow-100 rounded-lg px-3 py-2">
                  <Star className="w-4 h-4 text-yellow-600" />
                  <span className="text-yellow-700 text-sm font-medium">{statsLoading ? '...' : `${stats?.average_rating || 4.8}/5`}</span>
                </div>
                <div className="flex items-center gap-2 bg-green-100 rounded-lg px-3 py-2">
                  <Eye className="w-4 h-4 text-green-600" />
                  <span className="text-green-700 text-sm font-medium">{statsLoading ? '...' : `${stats?.total_tutorial_views || 0} Views`}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Featured Tutorials */}
        {featuredTutorials.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <Star className="w-6 h-6 text-yellow-500 fill-current" />
              <h2 className="text-lg font-bold text-gray-900">Featured Tutorials</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredTutorials.slice(0, 3).map((item) => (
                <How key={item.id} item={item} viewMode="grid" />
              ))}
            </div>
          </motion.div>
        )}

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8"
        >
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            {/* Search Bar */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search tutorials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2">
              {/* Category Filter */}
              <div className="relative">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 min-w-[120px]"
                >
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name} ({category.count})
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
              </div>

              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-md transition-all duration-200 ${
                    viewMode === 'grid' ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="Grid View"
                >
                  <Grid className="w-3 h-3" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded-md transition-all duration-200 ${
                    viewMode === 'list' ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="List View"
                >
                  <List className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>

          {/* Results Count and Quick Stats */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                {tutorialsLoading ? 'Loading...' : `${filteredData.length} tutorial${filteredData.length !== 1 ? 's' : ''} found`}
              </p>
              {!tutorialsLoading && (
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>Avg: 8 min</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    <span>Total: {tutorials?.reduce((sum, item) => sum + (item.views || 0), 0).toLocaleString()} views</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Content Grid */}
        <AnimatePresence mode="wait">
          {tutorialsLoading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={viewMode === 'list' ? 'space-y-4' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'}
            >
              {items.map((_, index) => (
                <HowItWorkSkeleton key={index} />
              ))}
            </motion.div>
          ) : filteredData.length > 0 ? (
            <motion.div
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={viewMode === 'list' ? 'space-y-4' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'}
            >
              {filteredData.map((item) => (
                <How key={item.id} item={item} viewMode={viewMode} />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center py-16"
            >
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 max-w-md mx-auto">
                <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Video className="w-8 h-8 text-gray-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">No Tutorials Found</h3>
                <p className="text-sm text-gray-600 mb-6">
                  {searchTerm || selectedCategory !== 'all' 
                    ? "Try adjusting your search terms or filters to find what you're looking for."
                    : "We're working on creating amazing tutorials for you. Check back soon!"
                  }
                </p>
                {(searchTerm || selectedCategory !== 'all') && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('all');
                    }}
                    className="bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white px-6 py-2 rounded-lg transition-colors duration-200"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Call to Action */}
        {!tutorialsLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-16 text-center"
          >
            <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-2xl p-8 border border-teal-200">
              <div className="max-w-2xl mx-auto">
                <div className="bg-gradient-to-r from-teal-400 to-teal-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Need More Help?
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Can't find what you're looking for? Our support team is here to help you with any questions or issues. 
                  We also offer personalized training sessions for teams and organizations.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => navigate('/contact')}
                    className="bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Contact Support
                  </button>
                  <button 
                    onClick={() => navigate('/faq')}
                    className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-8 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    <HelpCircle className="w-4 h-4" />
                    Browse FAQ
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
      
      <Footer />
    </div>
  )
}

export default HowItWorks